import { BuildingNode, LevelNode } from "@/app/features/reports/types/spaces"
import { Building, Floor, useAddAssetAreaMutation, useAddAssetLevelMutation, useAddBuildingMutation } from '@app.raytd.com/store'
import { useCallback } from "react"

interface UseSpaceCreationProps {
  asset_id: string | number
}

export function useSpaceCreation({ asset_id }: UseSpaceCreationProps) {
  const [createBuilding] = useAddBuildingMutation()
  const [createLevel] = useAddAssetLevelMutation()
  const [createArea] = useAddAssetAreaMutation()

  const handleCreateBuilding = useCallback(async (asset: undefined, building: string): Promise<BuildingNode> => {
    console.debug('Creating building', { building })
    const mutation = createBuilding({
      description: building,
      asset_id: asset_id
    })
    const result = await mutation
    console.debug('Building created', result)

    const payload = await mutation.unwrap()
    const createdBuilding = (payload && typeof payload === 'object' && 'data' in payload)
      ? (payload as any).data
      : payload

    const createdId = (createdBuilding as any)?.id ?? null
    if (!createdId) throw new Error('Building create succeeded but no id was returned')

    return {
      id: createdId,
      title: (createdBuilding as any)?.description ?? building,
      building_id: createdId,
      children: undefined,
      original: undefined
    }
  }, [createBuilding, asset_id])

  const handleCreateLevel = useCallback(async (building: Building, level: string): Promise<LevelNode> => {
    console.debug('Creating level', { building, level })

    const mutation = createLevel({
      description: level,
      asset_id: asset_id,
      building_id: building.id,
    })
    const result = await mutation

    console.debug('Level created', result)

    const payload = await mutation.unwrap()
    const createdLevel = (payload && typeof payload === 'object' && 'data' in payload)
      ? (payload as any).data
      : payload

    const createdId = (createdLevel as any)?.id ?? null
    if (!createdId) throw new Error('Level create succeeded but no id was returned')

    return {
      id: createdId,
      title: (createdLevel as any)?.description ?? level,
      building_id: (createdLevel as any)?.building_id ?? building.id,
      level_id: createdId,
      children: undefined,
      original: undefined
    }
  }, [createLevel, asset_id])

  const handleCreateArea = useCallback(async (path: Floor, area: string) => {
    console.debug('Creating area', { path, area })
    const mutation = createArea({
      description: area,
      asset_id: asset_id,
      building_id: path.building_id,
      level_id: path.id,
    })
    const result = await mutation
    console.debug('Area created', result)

    const payload = await mutation.unwrap()
    const createdArea = (payload && typeof payload === 'object' && 'data' in payload)
      ? (payload as any).data
      : payload

    const createdId = (createdArea as any)?.id ?? null
    if (!createdId) throw new Error('Area create succeeded but no id was returned')

    return {
      id: createdId,
      area_id: createdId,
      title: (createdArea as any)?.description ?? area,
      building_id: path.building_id,
      level_id: (createdArea as any)?.level_id ?? path.id,
      children: undefined,
      original: undefined
    }
  }, [createArea, asset_id])

  return {
    handleCreateBuilding,
    handleCreateLevel,
    handleCreateArea
  }
}